import React, { useEffect, useState } from "react";
import CourseAddModal from "./CourseAddModal";
import {
  ExtractedCourse,
  extractCourseFromImage,
} from "./extractCourseFromImage";
import { Course } from "./types";

interface Props {
  onSaved: () => void;
  onClose: () => void;
  // Persistence override, forwarded to the verify/save CourseAddModal so an
  // imported course can be written to the shared backend catalog (CGAdmin)
  // instead of the local coursesDb.
  persist?: (course: Course, mode: "create" | "update") => Promise<void>;
}

type Stage = "pick" | "preview" | "verify";

// Course-import subview. Pick image → backend OCR (gpt-4o vision via
// /caddiegames/extract-course) → editable verify modal that doubles as
// the save form (course name + per-hole values + tees).
export default function CourseImport({ onSaved, onClose, persist }: Props) {
  const [stage, setStage] = useState<Stage>("pick");
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extracted, setExtracted] = useState<ExtractedCourse | null>(null);

  // Object URLs leak unless revoked. Cleanup on unmount / replace.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handlePick = (f: File) => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setError(null);
    setStage("preview");
  };

  const handleExtract = async () => {
    if (!file) return;
    setExtracting(true);
    setError(null);
    try {
      const data = await extractCourseFromImage(file);
      setExtracted(data);
      setStage("verify");
    } catch (err) {
      setError((err as Error).message || "Could not read the scorecard");
    } finally {
      setExtracting(false);
    }
  };

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    setExtracted(null);
    setError(null);
    setStage("pick");
  };

  return (
    <div className="cg-subpage cg-import-subpage">
      <div className="cg-subpage-head">
        <button className="cg-btn cg-btn-ghost" onClick={onClose}>
          ← Back
        </button>
        <h2>Import scorecard</h2>
      </div>

      {stage === "pick" && (
        <div className="cg-import-pick">
          <p className="cg-subpage-note">
            Upload a photo of a printed scorecard. Extracted holes and
            tees will appear in an editable form so you can fix anything
            before saving.
          </p>
          <input
            type="file"
            accept="image/*"
            className="cg-import-file"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handlePick(f);
            }}
          />
        </div>
      )}

      {stage === "preview" && previewUrl && (
        <div className="cg-import-preview">
          <img
            src={previewUrl}
            alt="Selected scorecard"
            className="cg-import-thumb"
          />
          {error && <p className="cg-import-error">{error}</p>}
          <div className="cg-import-actions">
            <button
              type="button"
              className="cg-btn cg-btn-ghost"
              onClick={reset}
              disabled={extracting}
            >
              Pick another
            </button>
            <button
              type="button"
              className="cg-btn cg-btn-primary"
              onClick={handleExtract}
              disabled={extracting}
            >
              {extracting ? "Extracting…" : "Extract scorecard"}
            </button>
          </div>
        </div>
      )}

      {stage === "verify" && extracted && (
        <CourseAddModal
          title="Verify imported course"
          source="upload"
          persist={persist}
          initialName={extracted.name}
          initialHoles={extracted.holes}
          initialTees={extracted.tees}
          onSaved={() => {
            onSaved();
            onClose();
          }}
          onClose={reset}
        />
      )}
    </div>
  );
}
